/**
 * Bill export column spec + thin download wrappers. The CSV / Excel / PDF
 * plumbing lives in the shared `lib/table-export` module (the same one the
 * vendors export uses); this file only declares the bill columns.
 *
 * @see lib/table-export.ts — the shared serialiser
 */
import type { bills as billsContract } from "@flint/api-contract";
import {
  downloadBase64Pdf,
  downloadTableCsv,
  downloadTableXlsx,
  paiseToRupeeNumber,
  type ExportColumn,
} from "../../../lib/table-export.ts";

type ExportRow = billsContract.ExportRow;

const STATUS_LABEL: Record<ExportRow["status"], string> = {
  draft: "Draft",
  review: "In review",
  approval_pending: "Awaiting approval",
  ready_to_pay: "Ready to pay",
  paid: "Paid",
  void: "Void",
};

const statusLabel = (s: ExportRow["status"]): string => STATUS_LABEL[s];

const COLUMNS: ExportColumn<ExportRow>[] = [
  { header: "Vendor", csv: (r) => r.vendorName, xlsx: (r) => r.vendorName },
  { header: "Bill no.", csv: (r) => r.vendorDocNumber ?? "", xlsx: (r) => r.vendorDocNumber ?? "" },
  { header: "GSTIN", csv: (r) => r.vendorGstin ?? "", xlsx: (r) => r.vendorGstin ?? "" },
  { header: "City", csv: (r) => r.vendorCity ?? "", xlsx: (r) => r.vendorCity ?? "" },
  { header: "State", csv: (r) => r.vendorState ?? "", xlsx: (r) => r.vendorState ?? "" },
  { header: "Received", csv: (r) => r.receivedOn, xlsx: (r) => r.receivedOn },
  { header: "Due", csv: (r) => r.dueOn ?? "", xlsx: (r) => r.dueOn ?? "" },
  { header: "Status", csv: (r) => statusLabel(r.status), xlsx: (r) => statusLabel(r.status) },
  { header: "3-way match", csv: (r) => r.threeWayMatch, xlsx: (r) => r.threeWayMatch },
  { header: "Source", csv: (r) => r.source, xlsx: (r) => r.source },
  {
    header: "Amount (INR)",
    money: true,
    csv: (r) => paiseToRupeeNumber(r.totalPaise).toFixed(2),
    xlsx: (r) => paiseToRupeeNumber(r.totalPaise),
  },
  { header: "Added on", csv: (r) => r.createdAt.slice(0, 10), xlsx: (r) => r.createdAt.slice(0, 10) },
];

const fileStamp = (generatedAt: string): string => generatedAt.slice(0, 10);

export function downloadBillsCsv(rows: ExportRow[], generatedAt: string): void {
  downloadTableCsv(COLUMNS, rows, `bills-${fileStamp(generatedAt)}.csv`);
}

export function downloadBillsXlsx(rows: ExportRow[], generatedAt: string): Promise<void> {
  return downloadTableXlsx(COLUMNS, rows, {
    sheetName: "Bills",
    fileName: `bills-${fileStamp(generatedAt)}.xlsx`,
    isMuted: (r) => r.status === "void",
  });
}

/** PDF is server-rendered; just download the base64 payload. */
export function downloadBillsPdf(pdfBase64: string, fileName: string): void {
  downloadBase64Pdf(pdfBase64, fileName);
}
